import { DEFAULT_STARGATE_SERVICE_URL, PERMS_URL, ELIGIBLE_USERS } from './common';
import {
	type PermsApiRequest,
	type PermsApiResponse,
	type PermsApiResponseInterface,
	type UserRole,
} from '../types';
import { postJson } from './helpers';

export interface PermsApiClientInterface {
	getUserRole(cloudId: string): Promise<PermsApiResponseInterface>;
}

type GetUsersResponse = {
	[key: string]: string[];
};

export class PermsApiClient implements PermsApiClientInterface {
	private async isPermissionPermitted(request: PermsApiRequest): Promise<PermsApiResponse> {
		const path = `${DEFAULT_STARGATE_SERVICE_URL}${PERMS_URL}`;
		const { permissionId, cloudId } = request;
		const data = {
			permissionId,
			resourceId: `ari:cloud:platform::site/${cloudId}`,
		};
		return postJson<PermsApiResponse>(path, 'isPermissionPermitted', data);
	}

	public async getUsersWithoutAccess(
		cloudId: string,
		products: string[],
		accountIds: string[],
	): Promise<GetUsersResponse> {
		const path = `${DEFAULT_STARGATE_SERVICE_URL}${ELIGIBLE_USERS}`;
		const request = { cloudId, products, accountIds };
		return postJson(path, 'get-eligible-users', request);
	}

	public async getUserRole(cloudId: string): Promise<PermsApiResponseInterface> {
		try {
			const [{ permitted: isInviteUsersPermitted }, { permitted: isManagePermitted }] =
				await Promise.all([
					this.isPermissionPermitted({ permissionId: 'invite-users', cloudId }),
					this.isPermissionPermitted({ permissionId: 'manage', cloudId }),
				]);

			let role: UserRole;

			if (isInviteUsersPermitted && isManagePermitted) {
				role = 'admin';
			} else if (isInviteUsersPermitted && !isManagePermitted) {
				role = 'trusted';
			} else {
				role = 'basic';
			}

			return {
				role,
				isInviteUsersPermitted,
				isManagePermitted,
			};
		} catch (err) {
			//if something breaks while getting user role, fallback to 'basic'
			return {
				role: 'basic',
				errorMessage: `Error while getting user role: ${
					err instanceof Error ? err.message : String(err)
				}!`,
			};
		}
	}
}

export const defaultPermsApiClient = new PermsApiClient();
